import {
  Box,
  Button,
  ChevronLeftIcon,
  Cog6ToothIcon,
  Flex,
  Image,
  LocationDisplay,
  Panel,
  Text,
  UserAvatar,
} from 'components'
import { useEffect } from 'react'
import { useBreakpoint, useDisclosure, usePermission } from 'utils'
import useGoBack, { useUser } from 'utils/workspaceUtils'
import defaultImage from '../assets/default_tile.png'
import { FacilityManage, FacilitySettings } from '../components/Facility'
import { useFacility } from '../components/Facility/FacilityProvider'

export default function Facility() {
  const permission = usePermission()
  const { deleteFacility, facility, imageUrl, updateFacility } = useFacility()
  const manager = useUser(facility?.managerId)
  const goBack = useGoBack()

  const { open, onOpen, onClose } = useDisclosure()
  const breakpoint = useBreakpoint()

  useEffect(() => {
    if (permission.facility === 'viewer') return
    if (facility?.title === '') onOpen()
  }, [facility, permission.facility, onOpen])

  if (!facility) return null

  const { location, title, hasImage } = facility
  const isViewer = permission.facility === 'viewer'

  return (
    <Flex
      direction="column"
      gap={4}
      flex="auto"
      pt={{ base: 0, md: 3 }}
      pr={{ base: 0, md: 4 }}
      overflowY="auto"
      overflowX="hidden"
    >
      <Flex direction={{ base: 'column', lg: 'row' }} gap={4}>
        {breakpoint < 3 ? (
          <Flex
            height="200px"
            borderRadius={{ base: 0, md: '3xl' }}
            bgImage={`url(${hasImage ? imageUrl : defaultImage})`}
            bgSize="cover"
            bgPos="center"
            p={4}
            align="flex-start"
          >
            {breakpoint < 2 && (
              <Panel>
                <Button variant="ghost" onClick={goBack} leftIcon={<ChevronLeftIcon />}>
                  All Facilities
                </Button>
              </Panel>
            )}
          </Flex>
        ) : (
          <Image
            width="400px"
            borderRadius="3xl"
            height="200px"
            objectFit="cover"
            src={hasImage ? imageUrl : defaultImage}
          />
        )}

        <Flex overflow="hidden" direction="column" gap={6} mx={{ base: 4, md: 0 }}>
          <Flex direction={'column'} justify="space-between" gap={2}>
            <Text textStyle={{ base: 'h5', lg: 'h3' }}>{title ? title : '--'}</Text>
            {location && (
              <LocationDisplay
                location={location}
                iconProps={{ size: 'sm' }}
                textProps={{ textStyle: 'bodyMedium', fontWeight: 'normal', opacity: 1 }}
              />
            )}
            <Flex direction="column" alignItems="flex-start" gap={4} mt={1}>
              <Flex align="center" gap={2}>
                <Text textStyle="bodySmall">Facility Manager: </Text>
                <UserAvatar user={manager} />
                <Text textStyle="bodySmall">{manager?.displayName}</Text>
              </Flex>

              {!isViewer && (
                <Panel>
                  <Button variant="ghost" leftIcon={<Cog6ToothIcon />} onClick={onOpen}>
                    Manage Facility
                  </Button>
                </Panel>
              )}
            </Flex>
          </Flex>

          <FacilityManage
            facility={facility}
            open={open}
            onClose={async body => {
              onClose()
              if (!body) return

              await updateFacility({ ...body, id: facility.id })
            }}
            onDelete={deleteFacility}
            refetchImage={() => updateFacility(facility)}
          />
        </Flex>
      </Flex>

      <FacilitySettings />

      <Box />
    </Flex>
  )
}
